import {
    StyleSheet,
    Animated,
    TouchableOpacity,
    Text,
    View,
} from 'react-native';
import {Image} from 'expo-image';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {LayoutGrid} from 'lucide-react-native';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {ThemeType} from "@/theming/baseTheme";
import {AppDataOrganization} from "@/data/app-data-interfaces";
import {NotificationBell} from "@/components/notifications/NotificationBell";

// Create animated version of expo-image
const AnimatedImage = Animated.createAnimatedComponent(Image);

interface AnimatedHeaderProps {
    organization: AppDataOrganization;
    scrollY: Animated.Value;
    onSwitchOrg?: () => void;
    orgCount?: number;
}

// Animation constants
const HEADER_MAX_HEIGHT_OFFSET = 200;
const HEADER_MIN_HEIGHT_OFFSET = 60;

export const AnimatedHeader = ({
                                   organization,
                                   scrollY,
                                   onSwitchOrg,
                                   orgCount,
                               }: AnimatedHeaderProps) => {
    const {theme} = useCurrentTheme();
    const insets = useSafeAreaInsets();
    const styles = createStyles(theme, insets);

    // Calculate header heights with safe area
    const HEADER_MAX_HEIGHT = HEADER_MAX_HEIGHT_OFFSET + insets.top;
    const HEADER_MIN_HEIGHT = HEADER_MIN_HEIGHT_OFFSET + insets.top;
    const HEADER_SCROLL_DISTANCE = HEADER_MAX_HEIGHT - HEADER_MIN_HEIGHT;
    const contentHeight = HEADER_MAX_HEIGHT_OFFSET - 2 * theme.spacing.sm;

    // Animated values
    const headerHeight = scrollY.interpolate({
        inputRange: [0, HEADER_SCROLL_DISTANCE],
        outputRange: [HEADER_MAX_HEIGHT, HEADER_MIN_HEIGHT],
        extrapolate: 'clamp',
    });

    const iconHeight = scrollY.interpolate({
        inputRange: [0, HEADER_SCROLL_DISTANCE],
        outputRange: organization.iconOnlyHeader ? [contentHeight, 70] : [70, 40],
        extrapolate: 'clamp',
    });

    const iconWidth = scrollY.interpolate({
        inputRange: [0, HEADER_SCROLL_DISTANCE],
        outputRange: organization.iconOnlyHeader ? [contentHeight * 1.2, 84] : [84, 48],
        extrapolate: 'clamp',
    });

    const titleFontSize = scrollY.interpolate({
        inputRange: [0, HEADER_SCROLL_DISTANCE],
        outputRange: [theme.fontSize.xxl, theme.fontSize.lg],
        extrapolate: 'clamp',
    });

    const titleOpacity = scrollY.interpolate({
        inputRange: [0, HEADER_SCROLL_DISTANCE / 3],
        outputRange: [1, organization.iconOnlyHeader ? 0 : 1],
        extrapolate: 'clamp',
    });

    const subtitleOpacity = scrollY.interpolate({
        inputRange: [0, HEADER_SCROLL_DISTANCE / 3],
        outputRange: [1, 0],
        extrapolate: 'clamp',
    });

    const subtitleHeight = scrollY.interpolate({
        inputRange: [0, HEADER_SCROLL_DISTANCE / 3],
        outputRange: [theme.fontSize.sm * 1.2, 0],
        extrapolate: 'clamp',
    });

    const renderSwitchBadge = () => {
        if (!onSwitchOrg || !orgCount || orgCount <= 1) return null;

        return (
            <TouchableOpacity
                style={styles.switchBadge}
                onPress={onSwitchOrg}
                activeOpacity={0.7}
            >
                <LayoutGrid size={14} color={theme.colors.textSecondary}/>
                <Text style={styles.switchBadgeText}>{orgCount}</Text>
            </TouchableOpacity>
        );
    };

    // The header's actions, pinned to the top-right corner. The row owns the positioning so the
    // org switcher keeps exactly the place it had before the bell joined it, whether or not the
    // switcher is rendered at all.
    const renderHeaderActions = () => (
        <View style={styles.headerActions}>
            <NotificationBell/>
            {renderSwitchBadge()}
        </View>
    );

    const renderIconOnlyHeader = () => (
        <Animated.View style={styles.iconOnlyContainer}>
            {organization.icon && (
                <AnimatedImage
                    source={{uri: organization.icon}}
                    placeholder={{blurhash: organization.iconBlurhash}}
                    style={[
                        styles.headerIcon,
                        {
                            height: iconHeight,
                            width: iconWidth,
                        }
                    ]}
                    contentFit="contain"
                    cachePolicy="memory-disk"
                />
            )}
        </Animated.View>
    );

    const renderIconWithTextHeader = () => (
        <Animated.View style={styles.headerContent}>
            <Animated.View
                style={[
                    styles.headerRow,
                    {
                        opacity: titleOpacity,
                        justifyContent: organization.icon ? 'flex-start' : 'center'
                    }
                ]}
            >
                {organization.icon && (
                    <AnimatedImage
                        source={{uri: organization.icon}}
                        placeholder={{blurhash: organization.iconBlurhash}}
                        style={[
                            styles.headerIconSmall,
                            {
                                height: iconHeight,
                                width: iconWidth,
                            }
                        ]}
                        contentFit="contain"
                        cachePolicy="memory-disk"
                    />
                )}
                <Animated.View style={styles.headerTextContainer}>
                    <Animated.Text
                        style={[
                            styles.organizationName,
                            {fontSize: titleFontSize}
                        ]}
                    >
                        {organization.name}
                    </Animated.Text>

                    {organization.subtitle && (
                        <Animated.Text
                            style={[
                                styles.organizationSubtitle,
                                {
                                    opacity: subtitleOpacity,
                                    height: subtitleHeight,
                                    overflow: 'hidden'
                                }
                            ]}
                        >
                            {organization.subtitle}
                        </Animated.Text>
                    )}
                </Animated.View>
            </Animated.View>
        </Animated.View>
    );

    return (
        <Animated.View style={[styles.stickyHeader, {height: headerHeight}]}>
            {organization.iconOnlyHeader
                ? renderIconOnlyHeader()
                : renderIconWithTextHeader()
            }
            {renderHeaderActions()}
        </Animated.View>
    );
};

const createStyles = (theme: ThemeType, insets: {
    top: number;
    bottom: number;
    left: number;
    right: number;
}) => StyleSheet.create({
    stickyHeader: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: theme.colors.background,
        borderBottomWidth: 1,
        borderBottomColor: `${theme.colors.border}50`,
        paddingTop: insets.top + theme.spacing.sm,
        paddingHorizontal: theme.spacing.lg,
        paddingBottom: theme.spacing.sm,
        justifyContent: 'center',
        alignItems: 'center',
        shadowColor: theme.colors.shadow,
        shadowOffset: {width: 0, height: 2},
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 5,
    },

    headerContent: {
        flex: 1,
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
    },

    iconOnlyContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },

    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: theme.spacing.md,
    },

    headerTextContainer: {
        flex: 1,
        alignItems: 'flex-start',
    },

    headerIcon: {
        borderRadius: theme.borderRadius.lg,
    },

    headerIconSmall: {
        marginRight: theme.spacing.md,
        borderRadius: theme.borderRadius.md,
    },

    organizationName: {
        fontWeight: '800',
        color: theme.colors.primary,
        textAlign: 'left',
        marginBottom: theme.spacing.xs,
    },

    organizationSubtitle: {
        fontSize: theme.fontSize.sm,
        color: theme.colors.textSecondary,
        textAlign: 'left',
    },

    headerActions: {
        position: 'absolute',
        top: insets.top + theme.spacing.sm,
        right: theme.spacing.md,
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.sm,
        zIndex: 1,
    },

    switchBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors.border,
        borderRadius: theme.borderRadius.full,
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: theme.spacing.xs,
        gap: 4,
    },

    switchBadgeText: {
        fontSize: theme.fontSize.xs,
        fontWeight: '600',
        color: theme.colors.textSecondary,
    },
});

// Export header height constants for use in parent component
export const HEADER_CONSTANTS = {
    MAX_HEIGHT_OFFSET: HEADER_MAX_HEIGHT_OFFSET,
    MIN_HEIGHT_OFFSET: HEADER_MIN_HEIGHT_OFFSET,
};
